import {getSwitchedRoleStore} from '../components/header-components/UserRoleSwitcher'

/**
 * Utility function to extract user roles from Auth0 or Okta user object
 * Handles different claim structures between Auth0 and Okta
 *
 * @param {Object} user - User object from useAuth0 hook
 * @returns {Array<string>} - Array of user roles
 */
export function getUserRoles(user, context = 'default') {
  const {effectiveRole, isSwitchRoleUser} = getSwitchedRoleStore()

  if (isSwitchRoleUser && context === 'default') {
    return effectiveRole
  }

  if (!user) {
    return []
  }

  // Auth0 typically uses 'user_roles' or custom namespace claims
  // Okta might use different claim names like 'roles', 'groups', or namespaced claims

  // Check for user_roles (Auth0 standard)
  if (Array.isArray(user.user_roles)) {
    return user.user_roles
  }

  // Check for roles (common in Okta)
  if (Array.isArray(user.roles)) {
    return user.roles
  }

  // Check for groups (Okta alternative)
  if (Array.isArray(user.groups)) {
    return user.groups
  }

  // Check for namespaced claims (e.g., https://your-domain.com/roles)
  // Common pattern for custom claims in both Auth0 and Okta
  const namespacedRoleKeys = Object.keys(user).filter(
    key => key.includes('/roles') || key.includes('/user_roles'),
  )

  if (namespacedRoleKeys.length > 0) {
    const roles = user[namespacedRoleKeys[0]]
    if (Array.isArray(roles)) {
      return roles
    }
  }

  // Check for app_metadata or user_metadata (Auth0)
  if (user.app_metadata?.roles && Array.isArray(user.app_metadata.roles)) {
    return user.app_metadata.roles
  }

  if (user.user_metadata?.roles && Array.isArray(user.user_metadata.roles)) {
    return user.user_metadata.roles
  }

  // Return empty array if no roles found
  return []
}

/**
 * Check if user has a specific role
 *
 * @param {Object} user - User object from useAuth0 hook
 * @param {string} role - Role name to check
 * @returns {boolean} - True if user has the role
 */
export function hasRole(user, role) {
  const roles = getUserRoles(user)
  return roles.includes(role)
}

/**
 * Check if user has any of the specified roles
 *
 * @param {Object} user - User object from useAuth0 hook
 * @param {Array<string>} rolesToCheck - Array of role names to check
 * @returns {boolean} - True if user has any of the roles
 */
export function hasAnyRole(user, rolesToCheck) {
  const roles = getUserRoles(user)
  return rolesToCheck.some(role => roles?.includes(role))
}
